import { PerspectiveCamera, Quaternion, Vector3 } from "three";
import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import { FRAME_MODEL, type FrameTick, type FrameModel } from "@core/frame/frame";
import { isMobileDevice } from "@runtime/browser/mobileDevice";
import type { ThreeEngine } from "@runtime/engine/engineService";
import {
  ZERO_VIEWPORT_INSETS,
  approachViewportInsets,
  viewOffsetFromInsets,
  viewportInsetsEqual,
  type ViewportInsets,
} from "@models/ui/viewportInsets";
import { DefaultCameraSettings, type CameraSettings } from "./cameraSettings";
import { cameraScrollScheme, cameraWheelIntent, type CameraScrollScheme } from "./scrollScheme";
import { slerpVectors } from "./vectorSlerp";
import orbitCursorUrl from "@/assets/icons/RTH_Eye.png";
import panCursorUrl from "@/assets/icons/RTH_Hand.png";
import lookCursorUrl from "@/assets/icons/RTH_FreeMove.png";

export interface ViewChangedEvent {
  readonly position: Vector3;
  readonly target: Vector3;
  readonly radius: number;
  readonly alpha: number;
  readonly beta: number;
}

const LOOK_AROUND_PIXELS_PER_RADIAN = 250;
const ORBIT_PIXELS_PER_RADIAN = 250;
const UP = new Vector3(0, 1, 0);
const CURSOR_HOTSPOT_PX = 16;
const ORBIT_CURSOR = `url("${orbitCursorUrl}") ${CURSOR_HOTSPOT_PX} ${CURSOR_HOTSPOT_PX}, move`;
const PAN_CURSOR = `url("${panCursorUrl}") ${CURSOR_HOTSPOT_PX} ${CURSOR_HOTSPOT_PX}, move`;
const LOOK_CURSOR = `url("${lookCursorUrl}") ${CURSOR_HOTSPOT_PX} ${CURSOR_HOTSPOT_PX}, crosshair`;

/** Below this the focus target/radius are already where they should be — glide nothing. */
const FOCUS_EPSILON = 1e-6;

/**
 * Below this finger separation the distance ratio is dominated by touch jitter,
 * so the zoom leg of a pinch is skipped (the pan leg still runs).
 */
const K_MIN_PINCH_DISTANCE_PX = 8;

/**
 * A `WheelEvent` in LINE mode carries lines, not pixels. Firefox reports a
 * classic wheel notch as `deltaMode = 1, deltaY = 3`, where Chrome reports the
 * same notch as `deltaMode = 0, deltaY = 100` — so a handler that reads
 * `deltaY` raw is ~33x slower on one browser than the other. 40 px per line is
 * the widely used convention (it puts a Firefox notch at 120 px, next to
 * Chrome's 100) and is what makes `wheelPixels` a genuine normalization rather
 * than a second sensitivity constant.
 */
const K_WHEEL_LINE_HEIGHT_PX = 40;

/**
 * One classic wheel notch in CSS pixels after `wheelPixels` (Chrome's 100; a
 * Firefox notch normalizes to 120). The zoom counts notches against it, where
 * desktop counts `Input.mouseScrollDelta`, which is 1 per notch.
 */
const K_WHEEL_NOTCH_PX = 100;

/**
 * Time constant of the lens-shift approach. `1 - exp(-3)` is ~95%, so the
 * camera is visually settled in ~3x this — a shade over `--cf-motion-slow`
 * (260 ms), which is what the panels reveal and morph on, so the scene and the
 * panel that moved it come to rest together.
 */
/**
 * How much of the panel-clearing shift the SCENE declines, leaving the model
 * overlapping the panel by that share (user-directed 2026-09-01 — a full
 * clearance threw it at the far edge).
 *
 * ⚠ The scene's tolerance, not the measurement's: it renders THROUGH the
 * panel's glass, so overlap costs it nothing. The insets themselves stay exact,
 * which is what the video controls read — a button under a panel is invisible
 * and unclickable, and must keep its full clearance. See
 * `models/ui/viewportInsets.ts`.
 */
const K_SCENE_OVERLAP_FRACTION = 0.2;
const K_VIEW_OFFSET_TAU_S = 0.09;

/**
 * Below this many pixels a change in the shift is not worth re-deriving the
 * projection for, and is treated as settled. Half a CSS pixel: smaller than
 * anything a viewer can see, larger than the float noise a per-frame
 * `getBoundingClientRect` produces on a panel that is not moving.
 */
const K_VIEW_OFFSET_SETTLE_PX = 0.5;

/**
 * A wheel delta in CSS pixels, whatever unit the device reported it in.
 *
 * Both legs of `handleWheel` are in pixels — the pan multiplies by
 * world-units-per-PIXEL, and the zoom step is tuned against a ~100 px notch —
 * so this has to run before either of them.
 */
function wheelPixels(delta: number, deltaMode: number, viewportHeightPx: number): number {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * K_WHEEL_LINE_HEIGHT_PX;
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * viewportHeightPx;
  return delta;
}

export interface FocusOptions {
  /**
   * Glide to the framing instead of snapping to it. Defaults to `true`, which
   * matches the desktop client — Battlehub's `RuntimeSceneComponent.Focus`
   * always animates. Pass `false` for framing that has no meaningful "from"
   * pose (initial scene build).
   */
  readonly animate?: boolean;
  /** Override the transition length; defaults to `CameraSettings.focusTransitionSeconds`. */
  readonly durationSeconds?: number;
}

/**
 * Glide the orbit TARGET + RADIUS with the orientation held (Battlehub
 * `Focus`). EaseOutCubic.
 */
interface FocusTransition {
  readonly kind: "focus";
  readonly fromTarget: Vector3;
  readonly toTarget: Vector3;
  readonly fromRadius: number;
  readonly toRadius: number;
  readonly duration: number;
  elapsed: number;
}

/**
 * Move the WHOLE pose — camera position and orbit pivot — so the viewing
 * direction changes too. Port of `DesktopSceneModel.CoTransitionCamera`
 * (`DesktopSceneModel.cs:531`), which is what a scene switch and Set Scene
 * View both run.
 */
interface PoseTransition {
  readonly kind: "pose";
  readonly fromPosition: Vector3;
  readonly toPosition: Vector3;
  readonly fromTarget: Vector3;
  readonly toTarget: Vector3;
  readonly duration: number;
  elapsed: number;
}

/**
 * Turn the camera to face a point WITHOUT moving it. Port of
 * `DesktopSceneModel.CoTransitionCameraLookAt`, the animation behind the
 * `look_at` agent tool: the position is held, the viewing DIRECTION slerps on
 * a linear clock with the pivot kept at the starting look distance, and the
 * last frame drops the pivot onto the point itself.
 */
interface LookTransition {
  readonly kind: "look";
  readonly position: Vector3;
  /** Unit vector the camera started looking along. */
  readonly fromDirection: Vector3;
  /** `fromDirection` → the direction of the point; `setFromUnitVectors` copes with a 180° turn. */
  readonly rotation: Quaternion;
  readonly lookDistance: number;
  readonly toTarget: Vector3;
  readonly duration: number;
  elapsed: number;
}

type CameraTransition = FocusTransition | PoseTransition | LookTransition;

/** Live geometry of a two-finger gesture, in client (CSS) pixels. */
interface PinchState {
  readonly distance: number;
  readonly centerX: number;
  readonly centerY: number;
}

type LookMoveKey = "KeyW" | "KeyA" | "KeyS" | "KeyD" | "KeyQ" | "KeyE";

const LOOK_MOVE_KEYS = new Set<string>(["KeyW", "KeyA", "KeyS", "KeyD", "KeyQ", "KeyE"]);

export class OrbitCameraController {
  readonly camera: PerspectiveCamera;
  readonly onViewChanged = new Emitter<ViewChangedEvent>();

  private m_settings: CameraSettings;
  private m_canvas: HTMLCanvasElement | null = null;
  private readonly m_target = new Vector3();
  private m_alpha = 0;
  private m_beta = Math.PI / 2;
  private m_radius = 1;
  private m_mode: "none" | "orbit" | "look" | "pan" = "none";
  private m_pointerId = -1;
  private m_lastX = 0;
  private m_lastY = 0;
  private m_previousCursor = "";
  private m_ownsInteractionCursor = false;
  private m_frameOff: (() => void) | null = null;
  /** The continuous-render registration — see the constructor. */
  private m_renderSourceOff: (() => void) | null = null;
  private readonly m_lookMoveKeys = new Set<LookMoveKey>();
  private m_interactionSuppressed = false;
  /**
   * Which half of the desktop client's scroll behaviour this browser gets — see
   * `./scrollScheme`. Resolved ONCE, because `handleWheel` runs at wheel rate and
   * re-reading `navigator` + `localStorage` per event would be a storage hit per
   * notch for a value that cannot change without a reload.
   */
  private readonly m_scrollScheme: CameraScrollScheme = cameraScrollScheme();
  /**
   * The single in-flight camera animation. One slot on purpose: a focus glide
   * and a scene-view move must never run together, exactly as Unity shares
   * one `m_cameraResetCoroutine` handle across both of its transitions
   * (`DesktopSceneModel.cs:511-524` — each stops the other first).
   */
  private m_transition: CameraTransition | null = null;
  /** Live touch contacts, in press order (Map preserves insertion order). */
  private readonly m_touchPoints = new Map<number, { x: number; y: number }>();
  private m_pinch: PinchState | null = null;
  /** Mobile device families stay touch-driven even with a keyboard attached. */
  private readonly m_keyboardNavigationEnabled = !isMobileDevice();

  private readonly m_onWheel = (e: WheelEvent) => this.handleWheel(e);
  private readonly m_onContextMenu = (e: MouseEvent) => e.preventDefault();
  private readonly m_onPointerDown = (e: PointerEvent) => this.handlePointerDown(e);
  private readonly m_onPointerMove = (e: PointerEvent) => this.handlePointerMove(e);
  private readonly m_onPointerUp = (e: PointerEvent) => this.handlePointerUp(e);
  private readonly m_onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
  private readonly m_onKeyUp = (e: KeyboardEvent) => this.handleKeyUp(e);
  private readonly m_onBlur = () => {
    this.m_lookMoveKeys.clear();
    this.clearInteractionCursor();
  };
  private readonly m_onFrameTick = (tick: FrameTick) => this.handleFrameTick(tick);

  /**
   * Where the UI says the scene should be centred — see `setViewportInsets`.
   * `null` while nothing is covering the viewport, which is not the same as
   * zero insets: `null` also stops the easing, so the offset settles at exactly
   * nothing rather than asymptotically near it.
   */
  private m_insetsTarget: ViewportInsets | null = null;
  /** The eased value actually on the camera. Chases the target on the tick. */
  private m_insets: ViewportInsets = ZERO_VIEWPORT_INSETS;
  /** Last `setViewOffset` arguments, so an unchanged frame costs nothing. */
  private m_appliedOffset: { w: number; h: number; x: number; y: number } | null = null;
  private m_resizeOff: (() => void) | null = null;

  constructor(
    private readonly m_engine: ThreeEngine,
    settings: CameraSettings = DefaultCameraSettings,
  ) {
    this.m_settings = settings;
    this.camera = new PerspectiveCamera(
      settings.fovDegrees,
      getAspect(m_engine.canvas),
      settings.nearClip,
      settings.farClip,
    );
    this.camera.name = "campfire.orbitCamera";
    this.m_engine.scene.add(this.camera);
    this.applyDefaultPose();
    this.m_frameOff = Ioc.resolve<FrameModel>(FRAME_MODEL).onTick.on(this.m_onFrameTick);
    // Render on demand: every IMMEDIATE move requests its own frame
    // (`emitViewChanged`); these are the ones the tick carries out over
    // time — a flight, the lens shift easing to a new target, a WASD fly.
    this.m_renderSourceOff = this.m_engine.addContinuousRenderSource({
      rate: "full",
      label: "camera",
      isActive: () =>
        this.isTransitionActive
        || this.isViewOffsetEasing
        || (this.m_mode === "look" && this.m_lookMoveKeys.size > 0),
    });
    // ⚠ `EngineService.handleResize` sets `camera.aspect` and calls
    // `updateProjectionMatrix()` — and three PRESERVES `camera.view` across
    // that, so the stale `fullWidth`/`fullHeight` would keep scaling the offset
    // (`left += offsetX * width / fullWidth`) against the previous canvas. The
    // frame tick re-applies too, but a resize event can land between the tick
    // and the render, so this closes the one-frame window at its source.
    this.m_resizeOff = this.m_engine.addResizeListener(() => this.applyViewOffset());
  }

  /**
   * Centre the scene inside the part of the canvas that floating UI is NOT
   * covering — `null` (or all-zero) restores the whole canvas.
   *
   * ⚠ **Applied as a pure frustum SHEAR, never a sub-rect render.** The call is
   * `setViewOffset(W, H, dx, dy, W, H)`, where `width === fullWidth` and
   * `height === fullHeight` make three's scale terms exactly 1 — so only the
   * optical axis moves. Two things depend on that and would break if the extent
   * were narrowed to the free rect instead: the whole canvas must keep
   * rendering, because the user asked for the scene to stay visible BEHIND the
   * translucent panel; and every consumer that rebuilds the frustum
   * analytically from `fov` + `aspect` + distance rather than from the matrix
   * (`worldUnitsPerPixel` here, the constant-screen-size gizmo scales, the
   * `TransformControls` add-on we cannot patch) stays correct under a shift and
   * does not under a scale.
   *
   * The orbit TARGET is untouched in world space and needs no correction: the
   * camera still looks straight at it, so it now projects to the centre of the
   * free rect. That is the "orbit centre is also offsetted" half of the ask,
   * and it is why this is a projection change rather than a pose one — moving
   * the camera sideways instead would change the viewing angle, i.e. show a
   * different view of the model rather than the same view in a different place.
   */
  setViewportInsets(insets: ViewportInsets | null): void {
    this.m_insetsTarget = insets;
  }

  /** The viewport edges covered by floating UI, in CSS px, as last applied. */
  get viewportInsets(): ViewportInsets {
    return this.m_insets;
  }

  attachControl(canvas: HTMLCanvasElement): void {
    this.m_canvas = canvas;
    canvas.addEventListener("contextmenu", this.m_onContextMenu);
    canvas.addEventListener("wheel", this.m_onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.m_onPointerDown);
    canvas.addEventListener("pointermove", this.m_onPointerMove);
    canvas.addEventListener("pointerup", this.m_onPointerUp);
    canvas.addEventListener("pointercancel", this.m_onPointerUp);
    window.addEventListener("keydown", this.m_onKeyDown);
    window.addEventListener("keyup", this.m_onKeyUp);
    window.addEventListener("blur", this.m_onBlur);
  }

  detachControl(): void {
    const canvas = this.m_canvas;
    if (canvas) {
      canvas.removeEventListener("contextmenu", this.m_onContextMenu);
      canvas.removeEventListener("wheel", this.m_onWheel);
      canvas.removeEventListener("pointerdown", this.m_onPointerDown);
      canvas.removeEventListener("pointermove", this.m_onPointerMove);
      canvas.removeEventListener("pointerup", this.m_onPointerUp);
      canvas.removeEventListener("pointercancel", this.m_onPointerUp);
      window.removeEventListener("keydown", this.m_onKeyDown);
      window.removeEventListener("keyup", this.m_onKeyUp);
      window.removeEventListener("blur", this.m_onBlur);
    }
    this.clearInteractionCursor();
    this.m_canvas = null;
    this.m_mode = "none";
    this.m_pointerId = -1;
    this.m_lookMoveKeys.clear();
    this.m_touchPoints.clear();
    this.m_pinch = null;
    this.cancelTransition();
  }

  /**
   * Frame the camera on `target` at `radius`, gliding there by default.
   *
   * Port of Battlehub's `RuntimeSceneComponent.Focus` (the desktop path behind
   * Spark's `focus_on_part`): the orbit ORIENTATION never changes, so animating
   * the target + radius is exactly Unity's "lerp the camera position along its
   * own forward" — `position(t) = lerp(target) + lerp(radius)·dir` with `dir`
   * fixed by (alpha, beta) reduces to `lerp(startPosition, endPosition)`. Same
   * 1 s / EaseOutCubic shape.
   *
   * The glide is cancelled by any user camera input (orbit / look / wheel), by
   * an external pose take-over, and by a reset, so it can never fight the user.
   */
  focus(target: Vector3, radius?: number, options: FocusOptions = {}): void {
    const toRadius = radius !== undefined ? this.clampRadius(radius) : this.m_radius;
    const duration = options.durationSeconds ?? this.m_settings.focusTransitionSeconds;
    const isNoOp =
      this.m_target.distanceToSquared(target) < FOCUS_EPSILON &&
      Math.abs(this.m_radius - toRadius) < FOCUS_EPSILON;

    // `m_interactionSuppressed` means something else owns the pose (follow
    // mode) — a glide there would be overwritten every frame anyway.
    if (options.animate === false || duration <= 0 || isNoOp || this.m_interactionSuppressed) {
      this.cancelTransition();
      this.m_target.copy(target);
      this.m_radius = toRadius;
      this.updateCameraTransform();
      return;
    }

    // Start from the LIVE state, so re-focusing mid-glide redirects smoothly
    // instead of snapping back to the previous transition's origin.
    this.m_transition = {
      kind: "focus",
      fromTarget: this.m_target.clone(),
      toTarget: target.clone(),
      fromRadius: this.m_radius,
      toRadius,
      duration,
      elapsed: 0,
    };
  }

  /** True while ANY camera animation (focus glide or pose move) is running. */
  get isTransitionActive(): boolean {
    return this.m_transition !== null;
  }

  /**
   * The lens shift is still easing toward its target. NOT "the tick applied
   * an offset" — `tickViewOffset` re-applies it every frame, settled or not.
   */
  private get isViewOffsetEasing(): boolean {
    return !viewportInsetsEqual(
      this.m_insets,
      this.m_insetsTarget ?? ZERO_VIEWPORT_INSETS,
      K_VIEW_OFFSET_SETTLE_PX,
    );
  }

  /** True while a `focus` glide specifically is in flight. */
  get isFocusTransitionActive(): boolean {
    return this.m_transition?.kind === "focus";
  }

  /** Where the {@link transitionToPose} move in flight lands; null for none or a focus glide. */
  getPoseTransitionDestination(): { position: Vector3; target: Vector3 } | null {
    const transition = this.m_transition;
    if (transition?.kind !== "pose") return null;
    return { position: transition.toPosition.clone(), target: transition.toTarget.clone() };
  }

  /**
   * Move the camera to an exact pose — position AND orbit pivot — over
   * `durationSeconds`, so the viewing DIRECTION changes with it.
   *
   * Port of `DesktopSceneModel.CoTransitionCamera` (`:531`), the one animation
   * behind both Set Scene View and the camera move that accompanies a scene
   * change. Three details are upstream's and are load-bearing:
   *
   * - **Slerp, not lerp**, on both vectors. Unity uses `Vector3.Slerp`, which
   *   treats each as a vector FROM THE WORLD ORIGIN and interpolates its
   *   direction and its length separately — so the camera arcs around the
   *   scene instead of cutting a chord through it. The basis map between Unity
   *   and three (`M = diag(-1,1,1)`) is an origin-fixing isometry, so slerping
   *   in three space traces the same path as slerping in Unity space.
   * - **Linear time, no easing.** Upstream's local is named `QuadTime` and is
   *   plain `t / transitTime`; there is no curve. Do not "improve" it with the
   *   EaseOutCubic the focus glide uses — the two animations look different in
   *   the desktop client and should look different here.
   * - **A zero duration lands immediately.** Unity's `while (t < transitTime)`
   *   body never runs for `SceneTransitionSpeed.None`, and the trailing
   *   `ApplyCameraState` snaps.
   *
   * Deviation, deliberate and consistent with `focus`: the move is cancelled
   * by any user camera input, so it can never fight the user. Unity's
   * coroutine keeps writing the pose every frame and so effectively locks the
   * camera for the duration — unnoticeable at 0.1 s, not at the 4 s a VerySlow
   * scene change takes.
   */
  transitionToPose(position: Vector3, target: Vector3, durationSeconds: number): void {
    // Something else owns the pose (follow mode / XR) — a glide there would be
    // overwritten every frame anyway. Same guard `focus` uses.
    if (durationSeconds <= 0 || this.m_interactionSuppressed) {
      this.setPose(position, target);
      return;
    }
    this.m_transition = {
      kind: "pose",
      // Start from the LIVE pose so re-targeting mid-move redirects smoothly.
      fromPosition: this.camera.position.clone(),
      toPosition: position.clone(),
      fromTarget: this.m_target.clone(),
      toTarget: target.clone(),
      duration: durationSeconds,
      elapsed: 0,
    };
  }

  /**
   * Turn to face `target` from where the camera already is, over
   * `durationSeconds` — `look_at`. See {@link LookTransition}. As in Unity a
   * zero duration, or a point at the camera itself, lands at once; and like
   * every other move here, user camera input cancels it.
   */
  turnToFace(target: Vector3, durationSeconds: number): void {
    const position = this.camera.position.clone();
    const initialLook = this.m_target.clone().sub(position);
    if (initialLook.lengthSq() < 1e-6) this.camera.getWorldDirection(initialLook);
    const targetLook = target.clone().sub(position);
    if (durationSeconds <= 0 || targetLook.lengthSq() < 1e-6 || this.m_interactionSuppressed) {
      this.setPose(position, target);
      return;
    }
    // Unity floors the look distance at 0.1 so the pivot never sits on the lens.
    const lookDistance = Math.max(initialLook.length(), 0.1);
    const fromDirection = initialLook.normalize();
    this.m_transition = {
      kind: "look",
      position,
      fromDirection,
      rotation: new Quaternion().setFromUnitVectors(fromDirection, targetLook.normalize()),
      lookDistance,
      toTarget: target.clone(),
      duration: durationSeconds,
      elapsed: 0,
    };
  }

  /** Snapshot of the current pose (camera position + orbit target) for save/restore. */
  getPose(): { position: Vector3; target: Vector3 } {
    return { position: this.camera.position.clone(), target: this.m_target.clone() };
  }

  /** Restore a pose captured with `getPose` (recomputes orbit state from it). */
  setPose(position: Vector3, target: Vector3): void {
    this.cancelTransition();
    this.m_target.copy(target);
    this.recomputeOrbitFromPose(position, this.m_target);
    this.updateCameraTransform();
  }

  /**
   * Drive the camera to an exact world pose while user interaction is
   * suppressed (follow mode — port of Unity's `SetCameraLookAt` +
   * rotation overwrite in `DesktopLocalUserModel.FollowTarget`). Keeps the
   * orbit target one radius ahead so exiting the mode without a restore
   * still leaves a sane orbit state, and emits `onViewChanged` so the
   * multiuser transform publisher keeps broadcasting the follower's pose.
   */
  setExternalPose(position: Vector3, quaternion: Quaternion): void {
    this.cancelTransition();
    this.camera.position.copy(position);
    this.camera.quaternion.copy(quaternion);
    this.camera.updateMatrixWorld();
    const forward = new Vector3(0, 0, -1).applyQuaternion(quaternion);
    this.m_target
      .copy(position)
      .addScaledVector(forward, Math.max(this.m_radius, this.m_settings.minRadius));
    this.emitViewChanged();
  }

  resetToDefault(): void {
    this.applyDefaultPose();
  }

  setSettings(next: CameraSettings): void {
    this.m_settings = next;
    this.camera.near = next.nearClip;
    this.camera.far = next.farClip;
    this.camera.fov = next.fovDegrees;
    this.camera.updateProjectionMatrix();
    this.m_radius = this.clampRadius(this.m_radius);
    this.updateCameraTransform();
  }

  setInteractionSuppressed(suppressed: boolean): void {
    if (this.m_interactionSuppressed === suppressed) return;
    this.m_interactionSuppressed = suppressed;
    if (suppressed) {
      this.cancelTransition();
      this.m_mode = "none";
      this.m_pointerId = -1;
      this.clearInteractionCursor();
      this.m_lookMoveKeys.clear();
      this.m_touchPoints.clear();
      this.m_pinch = null;
    }
  }

  get settings(): CameraSettings {
    return this.m_settings;
  }

  dispose(): void {
    this.detachControl();
    this.m_frameOff?.();
    this.m_frameOff = null;
    this.m_renderSourceOff?.();
    this.m_renderSourceOff = null;
    this.m_resizeOff?.();
    this.m_resizeOff = null;
    this.onViewChanged.clear();
    this.camera.removeFromParent();
  }

  /**
   * The wheel ZOOMS or PANS depending on the platform, exactly as the desktop
   * client does — see `./scrollScheme` for the upstream line this comes from and
   * for the one deviation on the PC half (Shift, which is redundant upstream,
   * reaches pan so a trackpad with no middle button is not stranded).
   *
   *   - **Mac** — bare wheel pans, Shift + wheel zooms.
   *   - **PC** — bare wheel zooms, Shift + wheel pans.
   *
   * Both legs below are unchanged by the split; only which one a given event
   * reaches is new. Middle-button drag pans on both, as it does upstream.
   *
   * The ZOOM is desktop's (2026-10-05, user-directed parity): each notch moves
   * `wheelZoomPerNotch × max(wheelZoomNearDistance, distance to pivot)`, so 5%
   * of the distance far out and a fixed 0.5 m within 10 m, and it never stops
   * at the pivot. The web used to move 10% of the distance with the radius
   * clamped at `minRadius`, so near the pivot the steps shrank to nothing and
   * the camera stalled at the same spot every time (a saved scene view puts
   * the pivot back at the same point). One deviation: Battlehub lets the orbit
   * distance go negative and the camera pass THROUGH the pivot; here `dolly`
   * pushes the pivot ahead of the camera instead, because this controller
   * always orbits its target and a pivot behind the camera would orbit
   * inside-out. Desktop also tilts the zoom toward a part under the cursor
   * (`CampfireSceneComponent.BeginZoom`); that is not ported.
   *
   * ⚠ NOT ported from the same upstream file: `#if PLATFORM_STANDALONE_OSX`
   * doubles `m_orbitSens` and `m_freeRotateSens` on the Mac client. Both multiply
   * `Input.GetAxis("Mouse X"/"Mouse Y")`, whose scale is not the same on the two
   * platforms, and the doubling is undocumented and reaches neither `m_panSens`
   * (which multiplies the same axes) nor `m_orbitScrollSens` — so it reads as
   * COMPENSATION for the raw input rather than a wanted difference in feel. A
   * browser reports pointer deltas in CSS pixels identically on both platforms,
   * so porting the factor would CREATE an asymmetry upstream is removing. Orbit
   * sensitivity therefore stays one number here.
   */
  private handleWheel(event: WheelEvent): void {
    if (this.m_interactionSuppressed) return;
    event.preventDefault();
    this.cancelTransition();

    const deltaX = wheelPixels(event.deltaX, event.deltaMode, this.viewportHeight());
    const deltaY = wheelPixels(event.deltaY, event.deltaMode, this.viewportHeight());

    if (cameraWheelIntent(this.m_scrollScheme, event.shiftKey) === "zoom") {
      // `zoomDelta = scroll.y; if (zoomDelta == 0) zoomDelta = scroll.x;` —
      // verbatim upstream, so a device that reports a gesture on the
      // horizontal axis alone still zooms.
      const wheelDelta = deltaY !== 0 ? deltaY : deltaX;
      // Desktop's step (Battlehub `RuntimeSceneComponent.Zoom`): a share of the
      // distance to the pivot, but never less than the share of
      // `wheelZoomNearDistance`, i.e. 0.5 m a notch near the pivot. Positive
      // backs away, as scrolling down does.
      const notches = wheelDelta / K_WHEEL_NOTCH_PX;
      const step =
        notches
        * this.m_settings.wheelZoomPerNotch
        * Math.max(this.m_settings.wheelZoomNearDistance, this.m_radius);
      this.dolly(step);
      this.updateCameraTransform();
      return;
    }

    // The SAME world-units-per-pixel scale as the middle-button drag, because
    // upstream feeds the scroll into the very same `RuntimeSceneComponent.Pan()`
    // as the drag (it accumulates a synthetic mouse position from the scroll
    // and hands that to `Pan`). One consequence is worth stating: on a trackpad
    // the scene now tracks the fingers 1:1 in screen pixels, where the
    // `radius / 1000` approximation this replaces lagged them by ~30% at the
    // default FOV and was blind to both FOV and canvas height.
    const panScale = this.worldUnitsPerPixel() * this.m_settings.panSensitivity;
    const right = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
    const up = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
    this.m_target.addScaledVector(right, -deltaX * panScale);
    this.m_target.addScaledVector(up, deltaY * panScale);
    this.updateCameraTransform();
  }

  private handlePointerDown(event: PointerEvent): void {
    if (this.m_interactionSuppressed) return;

    if (event.pointerType === "touch") {
      this.m_touchPoints.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // A second finger turns the gesture into pinch-zoom + two-finger pan;
      // the single-finger orbit that was in flight is abandoned so the two
      // interpretations never fight over the same contact.
      if (this.m_touchPoints.size >= 2) {
        event.preventDefault();
        this.beginPinch();
        return;
      }
    }

    if (event.button !== 0 && event.button !== 1 && event.button !== 2) return;
    event.preventDefault();
    this.cancelTransition();
    this.m_mode = event.button === 2 ? "look" : event.button === 1 ? "pan" : "orbit";
    this.m_pointerId = event.pointerId;
    this.m_lastX = event.clientX;
    this.m_lastY = event.clientY;
    if (event.pointerType === "mouse") this.applyInteractionCursor();
    this.m_canvas?.setPointerCapture(event.pointerId);
  }

  private handlePointerMove(event: PointerEvent): void {
    if (this.m_interactionSuppressed) return;

    if (event.pointerType === "touch" && this.m_touchPoints.has(event.pointerId)) {
      this.m_touchPoints.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.m_pinch) {
        event.preventDefault();
        this.updatePinch();
        return;
      }
    }

    if (event.pointerId !== this.m_pointerId || this.m_mode === "none") return;
    event.preventDefault();

    const dx = event.clientX - this.m_lastX;
    const dy = event.clientY - this.m_lastY;
    this.m_lastX = event.clientX;
    this.m_lastY = event.clientY;

    if (this.m_mode === "orbit") {
      this.m_alpha += (dx * this.m_settings.orbitSensitivity) / ORBIT_PIXELS_PER_RADIAN;
      this.m_beta = clamp(
        this.m_beta - (dy * this.m_settings.orbitSensitivity) / ORBIT_PIXELS_PER_RADIAN,
        this.m_settings.minBeta,
        this.m_settings.maxBeta,
      );
      this.updateCameraTransform();
      return;
    }

    if (this.m_mode === "pan") {
      const panScale = this.worldUnitsPerPixel() * this.m_settings.panSensitivity;
      const right = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
      const up = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
      this.m_target.addScaledVector(right, -dx * panScale);
      this.m_target.addScaledVector(up, dy * panScale);
      this.updateCameraTransform();
      return;
    }

    const oldPosition = this.camera.position.clone();
    const screenToWorld = this.m_radius / LOOK_AROUND_PIXELS_PER_RADIAN;
    const right = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0).multiplyScalar(dx * screenToWorld);
    const up = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1).multiplyScalar(-dy * screenToWorld);
    const candidate = this.m_target.clone().add(right).add(up);
    const offset = candidate.sub(oldPosition);
    const distance = offset.length();
    if (distance > 1e-4) offset.multiplyScalar(this.m_radius / distance);
    this.m_target.copy(oldPosition).add(offset);
    this.recomputeOrbitFromPose(oldPosition, this.m_target);
    this.updateCameraTransform();
  }

  private handlePointerUp(event: PointerEvent): void {
    if (this.m_touchPoints.delete(event.pointerId) && this.m_pinch) {
      if (this.m_touchPoints.size >= 2) {
        // A third finger was down — keep pinching on whichever two remain.
        this.m_pinch = this.measurePinch();
      } else {
        this.m_pinch = null;
        this.releasePointerCapture(this.m_pointerId);
        const [remainingId, remaining] = this.m_touchPoints.entries().next().value ?? [];
        if (remainingId !== undefined && remaining) {
          // One finger left: hand the gesture back to orbit from where that
          // finger currently is, so the view doesn't jump by the pinch's span.
          this.m_mode = "orbit";
          this.m_pointerId = remainingId;
          this.m_lastX = remaining.x;
          this.m_lastY = remaining.y;
        } else {
          this.m_mode = "none";
          this.m_pointerId = -1;
        }
      }
      return;
    }

    if (event.pointerId !== this.m_pointerId) return;
    this.releasePointerCapture(event.pointerId);
    this.m_mode = "none";
    this.m_pointerId = -1;
    this.clearInteractionCursor();
  }

  /** Enter (or re-seed) the two-finger gesture, abandoning any orbit/look drag. */
  private beginPinch(): void {
    this.cancelTransition();
    this.releasePointerCapture(this.m_pointerId);
    this.m_mode = "none";
    this.m_pointerId = -1;
    this.clearInteractionCursor();
    this.m_pinch = this.measurePinch();
  }

  /**
   * Use the same centered cursor textures as Unity's
   * `RuntimeSceneComponent.UpdateCursorState`: eye for orbit, hand for pan,
   * and eye-with-motion marks for free look.
   */
  private applyInteractionCursor(): void {
    const canvas = this.m_canvas;
    if (!canvas) return;
    if (!this.m_ownsInteractionCursor) {
      this.m_previousCursor = canvas.style.cursor;
      this.m_ownsInteractionCursor = true;
    }
    canvas.style.cursor =
      this.m_mode === "pan" ? PAN_CURSOR : this.m_mode === "look" ? LOOK_CURSOR : ORBIT_CURSOR;
  }

  private clearInteractionCursor(): void {
    const canvas = this.m_canvas;
    if (!canvas || !this.m_ownsInteractionCursor) return;
    canvas.style.cursor = this.m_previousCursor;
    this.m_previousCursor = "";
    this.m_ownsInteractionCursor = false;
  }

  /**
   * Advance a live pinch: the finger-separation RATIO drives the orbit radius
   * (spread apart = closer) and the centroid drives the orbit target, so the
   * two legs compose the way they do in every native map/model viewer.
   */
  private updatePinch(): void {
    const previous = this.m_pinch;
    const next = this.measurePinch();
    if (!previous || !next) return;
    this.m_pinch = next;

    if (previous.distance > K_MIN_PINCH_DISTANCE_PX && next.distance > K_MIN_PINCH_DISTANCE_PX) {
      const ratio = previous.distance / next.distance;
      this.m_radius = this.clampRadius(
        this.m_radius * Math.pow(ratio, this.m_settings.pinchZoomSensitivity),
      );
    }

    const dx = next.centerX - previous.centerX;
    const dy = next.centerY - previous.centerY;
    if (dx !== 0 || dy !== 0) {
      // Pan in world units per CSS pixel at the target plane, so the scene
      // tracks the fingers instead of drifting with distance or FOV.
      const worldPerPixel = this.worldUnitsPerPixel();
      const right = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
      const up = new Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
      this.m_target.addScaledVector(right, -dx * worldPerPixel);
      this.m_target.addScaledVector(up, dy * worldPerPixel);
    }

    this.updateCameraTransform();
  }

  /** Separation + centroid of the two oldest live contacts, or null. */
  private measurePinch(): PinchState | null {
    const points = Array.from(this.m_touchPoints.values());
    if (points.length < 2) return null;
    const [a, b] = points;
    return {
      distance: Math.hypot(b.x - a.x, b.y - a.y),
      centerX: (a.x + b.x) / 2,
      centerY: (a.y + b.y) / 2,
    };
  }

  /**
   * Ease the live insets toward the target and push the result at the camera.
   *
   * Eased rather than snapped because the insets change on real events the user
   * is watching — a step gaining a media card, a panel being dragged, a run
   * starting — and every one of those already animates. `K_VIEW_OFFSET_TAU_S`
   * is picked so the camera settles over roughly the same span as the panel
   * reveal it is reacting to, and the exponential form is what lets it TRACK a
   * moving target (a drag) instead of chasing a series of step changes.
   */
  private tickViewOffset(tick: FrameTick): void {
    const target = this.m_insetsTarget ?? ZERO_VIEWPORT_INSETS;
    if (viewportInsetsEqual(this.m_insets, target, K_VIEW_OFFSET_SETTLE_PX)) {
      // Land exactly, so a settled camera stops re-deriving a projection that
      // is asymptotically-but-never-quite the resting one.
      this.m_insets = target;
    } else {
      const t = 1 - Math.exp(-Math.max(0, tick.deltaSeconds) / K_VIEW_OFFSET_TAU_S);
      this.m_insets = approachViewportInsets(this.m_insets, target, t);
    }
    this.applyViewOffset();
  }

  /**
   * Write the current insets onto the camera as a frustum shear.
   *
   * ⚠ Never while an XR session presents. `WebXRManager` overwrites
   * `projectionMatrix` and `fov` from the session's own views every presented
   * frame and never reads `camera.view`, so the offset is inert there — but
   * `setViewOffset` also ASSIGNS `camera.aspect`, and running it per frame
   * against a canvas three is sizing for the headset would fight it. The offset
   * is re-applied on session end by the ordinary tick.
   */
  private applyViewOffset(): void {
    if (this.m_engine.renderer.xr.isPresenting) return;
    const canvas = this.m_canvas ?? this.m_engine.canvas;
    const w = Math.max(1, canvas.clientWidth);
    const h = Math.max(1, canvas.clientHeight);
    const offset = viewOffsetFromInsets(this.m_insets, K_SCENE_OVERLAP_FRACTION);

    if (Math.abs(offset.x) < K_VIEW_OFFSET_SETTLE_PX && Math.abs(offset.y) < K_VIEW_OFFSET_SETTLE_PX) {
      if (this.m_appliedOffset === null) return;
      this.m_appliedOffset = null;
      this.camera.clearViewOffset();
      return;
    }

    const applied = this.m_appliedOffset;
    if (
      applied !== null
      && applied.w === w
      && applied.h === h
      && Math.abs(applied.x - offset.x) < K_VIEW_OFFSET_SETTLE_PX
      && Math.abs(applied.y - offset.y) < K_VIEW_OFFSET_SETTLE_PX
    ) {
      return;
    }
    this.m_appliedOffset = { w, h, x: offset.x, y: offset.y };
    // `fullWidth`/`fullHeight` equal `width`/`height`, so three's
    // `width *= view.width / view.fullWidth` is 1 and nothing is scaled. It
    // also re-assigns `camera.aspect` to `w / h` — the same value
    // `EngineService.handleResize` sets — and calls `updateProjectionMatrix()`.
    this.camera.setViewOffset(w, h, offset.x, offset.y, w, h);
  }

  private worldUnitsPerPixel(): number {
    return (
      (2 * this.m_radius * Math.tan((this.camera.fov * Math.PI) / 360)) / this.viewportHeight()
    );
  }

  /** CSS pixel height of the drawing surface; never 0, so it is safe to divide by. */
  private viewportHeight(): number {
    return Math.max(1, this.m_canvas?.clientHeight ?? this.m_engine.canvas.clientHeight);
  }

  private releasePointerCapture(pointerId: number): void {
    if (pointerId < 0) return;
    // `hasPointerCapture` guards the DOMException a release on an already
    // released (or never captured) pointer throws.
    if (this.m_canvas?.hasPointerCapture(pointerId)) this.m_canvas.releasePointerCapture(pointerId);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.m_keyboardNavigationEnabled) return;
    if (this.m_mode !== "look") return;
    if (!LOOK_MOVE_KEYS.has(event.code)) return;
    event.preventDefault();
    this.m_lookMoveKeys.add(event.code as LookMoveKey);
  }

  private handleKeyUp(event: KeyboardEvent): void {
    if (!this.m_keyboardNavigationEnabled) return;
    if (LOOK_MOVE_KEYS.has(event.code)) this.m_lookMoveKeys.delete(event.code as LookMoveKey);
  }

  private handleFrameTick(tick: FrameTick): void {
    this.tickTransition(tick);
    // Above the guards below on purpose: the projection has to keep following
    // the UI while the camera is suppressed (a follow, an XR session) and
    // whatever the pointer mode is. It runs before the render — `renderFrame`
    // is `tick()` then draw then `lateTick()`.
    this.tickViewOffset(tick);
    if (this.m_interactionSuppressed) return;
    if (this.m_mode !== "look" || this.m_lookMoveKeys.size === 0) return;

    const speed = Math.max(1, this.m_radius) * this.m_settings.lookMoveSpeed * tick.deltaSeconds;
    const forward = new Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();
    const right = new Vector3().crossVectors(forward, UP).normalize();
    const delta = new Vector3();
    if (this.m_lookMoveKeys.has("KeyW")) delta.add(forward);
    if (this.m_lookMoveKeys.has("KeyS")) delta.sub(forward);
    if (this.m_lookMoveKeys.has("KeyD")) delta.add(right);
    if (this.m_lookMoveKeys.has("KeyA")) delta.sub(right);
    if (this.m_lookMoveKeys.has("KeyE")) delta.add(UP);
    if (this.m_lookMoveKeys.has("KeyQ")) delta.sub(UP);
    if (delta.lengthSq() === 0) return;

    delta.normalize().multiplyScalar(speed);
    this.camera.position.add(delta);
    this.m_target.add(delta);
    this.emitViewChanged();
  }

  /**
   * Advance an in-flight `focus` glide. Interpolating the orbit target + radius
   * (with alpha/beta held) reproduces Unity's straight-line camera lerp, and
   * keeps the camera looking at the framing the whole way instead of swinging.
   */
  private tickTransition(tick: FrameTick): void {
    const transition = this.m_transition;
    if (!transition) return;

    transition.elapsed += tick.deltaSeconds;
    const t = clamp(transition.elapsed / transition.duration, 0, 1);
    const done = t >= 1;

    if (transition.kind === "focus") {
      const eased = easeOutCubic(t);
      this.m_target.lerpVectors(transition.fromTarget, transition.toTarget, eased);
      this.m_radius =
        transition.fromRadius + (transition.toRadius - transition.fromRadius) * eased;
      if (done) {
        // Land on the exact destination — easing at t=1 is 1, but don't leave
        // the final pose to floating-point luck.
        this.m_target.copy(transition.toTarget);
        this.m_radius = transition.toRadius;
        this.m_transition = null;
      }
      this.updateCameraTransform();
      return;
    }

    if (transition.kind === "look") {
      if (done) {
        this.m_target.copy(transition.toTarget);
        this.m_transition = null;
      } else {
        s_scratchRotation.identity().slerp(transition.rotation, t);
        s_scratchDirection.copy(transition.fromDirection).applyQuaternion(s_scratchRotation);
        this.m_target
          .copy(transition.position)
          .addScaledVector(s_scratchDirection, transition.lookDistance);
      }
      this.recomputeOrbitFromPose(transition.position, this.m_target);
      this.updateCameraTransform();
      return;
    }

    // Pose move: slerp both world vectors on a LINEAR clock, then rebuild the
    // orbit state from the pair. `updateCameraTransform` recomputes the camera
    // position from (target, alpha, beta, radius) and would throw the slerped
    // position away, so this branch goes through `recomputeOrbitFromPose`.
    if (done) {
      this.m_target.copy(transition.toTarget);
      this.recomputeOrbitFromPose(transition.toPosition, this.m_target);
      this.m_transition = null;
    } else {
      slerpVectors(transition.fromTarget, transition.toTarget, t, this.m_target);
      const position = slerpVectors(
        transition.fromPosition,
        transition.toPosition,
        t,
        s_scratchPosition,
      );
      this.recomputeOrbitFromPose(position, this.m_target);
    }
    this.updateCameraTransform();
  }

  private cancelTransition(): void {
    this.m_transition = null;
  }

  private applyDefaultPose(): void {
    this.cancelTransition();
    // Place the camera at the literal default position (ported from Unity's
    // SceneView Variant prefab, converted to Three space) and let
    // `recomputeOrbitFromPose` derive the orbit radius from it. Do NOT rescale
    // the offset to `defaultRadius` — that discarded the prefab's distance and
    // pinned the camera 1 unit from the target, at the wrong elevation.
    this.m_target.fromArray(this.m_settings.defaultTarget);
    const position = new Vector3().fromArray(this.m_settings.defaultPosition);
    this.recomputeOrbitFromPose(position, this.m_target);
    this.updateCameraTransform();
  }

  private updateCameraTransform(): void {
    this.m_radius = this.clampRadius(this.m_radius);
    this.m_beta = clamp(this.m_beta, this.m_settings.minBeta, this.m_settings.maxBeta);
    const sinBeta = Math.sin(this.m_beta);
    this.camera.position.set(
      this.m_target.x + this.m_radius * sinBeta * Math.cos(this.m_alpha),
      this.m_target.y + this.m_radius * Math.cos(this.m_beta),
      this.m_target.z + this.m_radius * sinBeta * Math.sin(this.m_alpha),
    );
    this.camera.lookAt(this.m_target);
    this.camera.updateMatrixWorld();
    this.emitViewChanged();
  }

  private recomputeOrbitFromPose(position: Vector3, target: Vector3): void {
    const offset = position.clone().sub(target);
    // Floor the length so a degenerate (camera == target) pose has a usable
    // direction, but derive the ELEVATION from that true length rather than
    // from the clamped radius. Dividing by the clamped value conflates two
    // separate corrections: a pose further out than `maxRadius` would lose its
    // viewing ANGLE as well as its distance — e.g. a 45° view at 1202 units
    // restores nearly overhead once the radius is pinned to 500. That never
    // bit before, because every caller restored a pose `getPose()` had already
    // produced (and so already clamped); a scene view authored on the desktop
    // client, where Battlehub bounds nothing, is the first genuinely external
    // pose to arrive here.
    const length = Math.max(offset.length(), this.m_settings.minRadius);
    this.m_radius = this.clampRadius(length);
    this.m_alpha = Math.atan2(offset.z, offset.x);
    this.m_beta = Math.acos(clamp(offset.y / length, -1, 1));
  }

  private emitViewChanged(): void {
    // Every camera move ends here — input, a pose, a follow, the WASD fly —
    // so this is the one place it asks render on demand for a frame.
    this.m_engine.requestRender();
    this.onViewChanged.emit({
      position: this.camera.position.clone(),
      target: this.m_target.clone(),
      radius: this.m_radius,
      alpha: this.m_alpha,
      beta: this.m_beta,
    });
  }

  private clampRadius(radius: number): number {
    return clamp(radius, this.m_settings.minRadius, this.m_settings.maxRadius);
  }

  /**
   * Moves the camera `distance` along its line of sight: positive backs away
   * from the pivot, negative closes on it. Closing past `minRadius` pushes the
   * pivot forward by the rest, so the camera keeps going instead of stopping
   * there; the caller applies the pose.
   */
  private dolly(distance: number): void {
    const radius = this.m_radius + distance;
    if (radius >= this.m_settings.minRadius) {
      this.m_radius = this.clampRadius(radius);
      return;
    }
    // The camera sits at `target + radius · dir`; moving the target along
    // `-dir` by the overshoot moves the camera by exactly `distance`.
    const overshoot = this.m_settings.minRadius - radius;
    const sinBeta = Math.sin(this.m_beta);
    this.m_target.x -= overshoot * sinBeta * Math.cos(this.m_alpha);
    this.m_target.y -= overshoot * Math.cos(this.m_beta);
    this.m_target.z -= overshoot * sinBeta * Math.sin(this.m_alpha);
    this.m_radius = this.m_settings.minRadius;
  }
}

function getAspect(canvas: HTMLCanvasElement): number {
  return Math.max(1, canvas.clientWidth) / Math.max(1, canvas.clientHeight);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Scratch for the pose slerp — one allocation instead of one per frame. */
const s_scratchPosition = new Vector3();
const s_scratchDirection = new Vector3();
const s_scratchRotation = new Quaternion();

/** Battlehub `Run.EaseOutCubic`: `(--t) * t * t + 1`. */
function easeOutCubic(t: number): number {
  const inv = t - 1;
  return inv * inv * inv + 1;
}

export const ORBIT_CAMERA = Symbol.for("OrbitCameraController");
